<template>
  <main id="pesanan-page" class="nuxt-page">
    <!-- Background -->
    <div class="page-bg">
      <div class="page-gradient-1"></div>
      <div class="page-gradient-2"></div>
      <div class="page-grid"></div>
    </div>

    <section class="pesanan-section">
      <div class="pesanan-container">
        <!-- Header -->
        <div class="page-header">
          <span class="page-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
            Daftar Layanan
          </span>
          <h1 class="page-title">Pilih Layanan <span class="text-gradient">Yohom</span></h1>
          <p class="page-subtitle">Silakan pilih kategori layanan yang Anda butuhkan. Klik item untuk langsung terhubung via WhatsApp.</p>
        </div>

        <!-- Lokasi User -->
        <div class="location-banner" :class="{ 'location-banner--error': locationError }">
          <div class="location-icon">
            <div v-if="locationLoading" class="location-spinner"></div>
            <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
          </div>
          <div class="location-text">
            <span class="location-label">Lokasi Anda</span>
            <span v-if="locationLoading" class="location-value">Mendeteksi lokasi...</span>
            <span v-else-if="locationError" class="location-value">{{ locationError }}</span>
            <span v-else class="location-value">{{ userCity }}</span>
          </div>
          <button v-if="!locationLoading" class="location-refresh" @click="detectLocation" title="Deteksi ulang lokasi">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </button>
        </div>

        <!-- Loading -->
        <div v-if="loadingPekerjaan" class="loading-state">
          <div class="loading-spinner"></div>
          <p class="loading-text">Memuat layanan...</p>
        </div>

        <!-- Belum tersedia di kota ini -->
        <div v-else-if="pekerjaans.length === 0" class="empty-state">
          <div class="empty-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
            </svg>
          </div>
          <p class="empty-title">Layanan belum tersedia</p>
          <p class="empty-desc">
            Maaf, layanan Yohom belum menjangkau
            <template v-if="userCity"> wilayah <strong>{{ userCity }}</strong></template>
            <template v-else> lokasi Anda</template>. Kami akan segera hadir!
          </p>
        </div>

        <!-- Categories -->
        <div v-else class="categories-list">
          <div
            v-for="(pekerjaan, index) in pekerjaans"
            :key="index"
            class="category-card"
          >
            <div
              class="category-header"
              @click="toggleCategory(index)"
              :class="{ 'category-header--active': activeCategory === index }"
            >
              <div class="category-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="3" width="7" height="7"/>
                  <rect x="14" y="3" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/>
                </svg>
              </div>
              <h3 class="category-name">{{ pekerjaan.kategori_nama }}</h3>
              <div class="category-count" v-if="pekerjaan.pekerjaan">
                {{ pekerjaan.pekerjaan.length }} layanan
              </div>
              <div class="category-chevron">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>
            </div>

            <!-- Sub-items (Pekerjaan) -->
            <transition name="slide">
              <div v-if="activeCategory === index && pekerjaan.pekerjaan && pekerjaan.pekerjaan.length > 0" class="category-body">
                <div
                  v-for="(pekerjaanItem, idxP) in pekerjaan.pekerjaan"
                  :key="idxP"
                  class="pekerjaan-item"
                >
                  <div
                    class="pekerjaan-header"
                    @click.stop="togglePekerjaan(index + '-' + idxP)"
                    :class="{ 'pekerjaan-header--active': activePekerjaan === index + '-' + idxP }"
                  >
                    <img
                      :src="pekerjaanItem.gbr_pekerjaan"
                      :alt="pekerjaanItem.nama_pekerjaan"
                      class="pekerjaan-img"
                    />
                    <span class="pekerjaan-name">{{ pekerjaanItem.nama_pekerjaan }}</span>
                    <div class="pekerjaan-chevron">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </div>
                  </div>

                  <!-- Keterangan items -->
                  <transition name="slide">
                    <div v-if="activePekerjaan === index + '-' + idxP && pekerjaanItem.keterangan" class="keterangan-list">
                      <div
                        v-for="(ket, idxK) in pekerjaanItem.keterangan"
                        :key="idxK"
                        class="ket-card"
                        @click.stop="directWA(ket)"
                      >
                        <div class="ket-info">
                          <p class="ket-name">{{ ket.ket_pekerjaan }}</p>
                          <p class="ket-price">Rp {{ formatNumber(ket.harga) }}</p>
                        </div>
                        <div class="ket-wa-btn">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <!-- Bottom Info -->
        <div class="bottom-info">
          <div class="info-card">
            <div class="info-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
            </div>
            <div>
              <p class="info-title">Butuh bantuan khusus?</p>
              <p class="info-desc">Hubungi kami via WhatsApp untuk konsultasi gratis.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
export default {
  name: "SimulasiPesanan",
  data() {
    return {
      pekerjaans: [],
      activeCategory: null,
      activePekerjaan: null,
      userCity: "",
      locationLoading: false,
      locationError: "",
      loadingPekerjaan: true,
      config: {
        headers: {
          api_key: "b2e77a7c2d6bb1630b5df98624bc885a",
        },
      },
    };
  },
  mounted() {
    // Daftar pekerjaan mengikuti kota hasil geolokasi.
    // Jika lokasi gagal/ditolak, getPekerjaan dipanggil dengan wilayah default.
    this.detectLocation();
  },
  methods: {
    detectLocation() {
      if (!("geolocation" in navigator)) {
        this.locationError = "Geolocation tidak didukung browser ini";
        this.getPekerjaan();
        return;
      }

      this.locationLoading = true;
      this.locationError = "";

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&accept-language=id`
            );
            const data = await response.json();
            const addr = data.address || {};
            this.userCity =
              addr.city ||
              addr.town ||
              addr.city_district ||
              addr.county ||
              addr.municipality ||
              addr.state ||
              "";
          } catch (error) {
            console.error(error);
            this.locationError = "Gagal mengambil nama kota";
          } finally {
            this.locationLoading = false;
            // Ambil layanan sesuai kota. Jika kota tidak terdeteksi,
            // pakai wilayah default agar halaman tetap terisi.
            this.getPekerjaan(this.userCity || null);
          }
        },
        (error) => {
          console.error(error);
          this.locationLoading = false;
          if (error.code === error.PERMISSION_DENIED) {
            this.locationError = "Izin lokasi ditolak";
          } else {
            this.locationError = "Lokasi tidak tersedia";
          }
          this.getPekerjaan();
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    },
    toggleCategory(index) {
      this.activeCategory = this.activeCategory === index ? null : index;
      this.activePekerjaan = null;
    },
    togglePekerjaan(key) {
      this.activePekerjaan = this.activePekerjaan === key ? null : key;
    },
    async getPekerjaan(namaWilayah = null) {
      this.loadingPekerjaan = true;
      this.pekerjaans = [];

      const form = new FormData();
      if (namaWilayah) {
        // Filter berdasarkan kota hasil geolokasi.
        form.append("nama_wilayah", namaWilayah);
      } else {
        // Fallback wilayah default bila lokasi tidak tersedia.
        form.append("wilayah_id", "1");
      }

      try {
        const response = await fetch(
          "https://api.yohom.co.id/pelanggan/pekerjaan",
          {
            method: "POST",
            body: form,
            headers: {
              api_key: "b2e77a7c2d6bb1630b5df98624bc885a",
            },
          }
        );
        const data = await response.json();
        this.pekerjaans = Array.isArray(data.res) ? data.res : [];
      } catch (error) {
        console.error(error);
        this.pekerjaans = [];
      } finally {
        this.loadingPekerjaan = false;
      }
    },
    formatNumber(number) {
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    directWA(pekerjaan) {
      window.open(
        "https://wa.me/6282228502424?&text=Hai Yohom, Saya mau melakukan pemesanan jasa: " +
          pekerjaan.ket_pekerjaan +
          " (Rp " +
          this.formatNumber(pekerjaan.harga) +
          "), Bisakah anda membantu?",
        "_blank"
      );
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Public+Sans:wght@400;500;600;700;800&display=swap');

.nuxt-page {
  font-family: 'Public Sans', sans-serif;
  min-height: 100vh;
  background: #fcfcfd;
  position: relative;
  overflow: hidden;
  padding: 100px 16px 60px;
  -webkit-font-smoothing: antialiased;
}

/* Background */
.page-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.page-gradient-1 {
  position: absolute;
  top: -150px;
  right: -80px;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(245, 158, 11, 0.07) 0%, transparent 70%);
  animation: bgFloat 8s ease-in-out infinite;
}

.page-gradient-2 {
  position: absolute;
  bottom: -100px;
  left: -60px;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.06) 0%, transparent 70%);
  animation: bgFloat 10s ease-in-out infinite reverse;
}

.page-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(0, 0, 0, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 0, 0, 0.02) 1px, transparent 1px);
  background-size: 64px 64px;
  mask-image: radial-gradient(ellipse 70% 50% at 50% 30%, black 20%, transparent 100%);
  -webkit-mask-image: radial-gradient(ellipse 70% 50% at 50% 30%, black 20%, transparent 100%);
}

@keyframes bgFloat {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-20px) scale(1.03); }
}

/* Container */
.pesanan-section {
  position: relative;
  z-index: 1;
}

.pesanan-container {
  max-width: 760px;
  margin: 0 auto;
}

/* Header */
.page-header {
  text-align: center;
  margin-bottom: 48px;
}

.page-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 100px;
  background: rgba(245, 158, 11, 0.08);
  color: #d97706;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  border: 1px solid rgba(245, 158, 11, 0.15);
  margin-bottom: 20px;
}

.page-title {
  font-size: 36px;
  font-weight: 800;
  color: #111827;
  line-height: 1.2;
  letter-spacing: -0.025em;
  margin: 0 0 12px;
}

.text-gradient {
  background: linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.page-subtitle {
  font-size: 16px;
  color: #6b7280;
  line-height: 1.6;
  max-width: 520px;
  margin: 0 auto;
}

/* Loading */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 60px 0;
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #10b981;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 15px;
  color: #9ca3af;
  margin: 0;
}

/* Location Banner */
.location-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  margin-bottom: 16px;
  background: rgba(16, 185, 129, 0.05);
  border: 1px solid rgba(16, 185, 129, 0.15);
  border-radius: 14px;
  transition: all 0.2s ease;
}

.location-banner--error {
  background: rgba(239, 68, 68, 0.04);
  border-color: rgba(239, 68, 68, 0.15);
}

.location-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
  flex-shrink: 0;
}

.location-banner--error .location-icon {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.location-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(16, 185, 129, 0.25);
  border-top-color: #059669;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.location-text {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.location-label {
  font-size: 12px;
  font-weight: 500;
  color: #9ca3af;
  letter-spacing: 0.01em;
}

.location-value {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}

.location-banner--error .location-value {
  color: #dc2626;
  font-weight: 600;
  font-size: 14px;
}

.location-refresh {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.location-refresh:hover {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

/* Empty / belum tersedia */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding: 48px 24px;
  background: #ffffff;
  border: 1px dashed #e5e7eb;
  border-radius: 14px;
}

.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: rgba(245, 158, 11, 0.08);
  color: #d97706;
  margin-bottom: 8px;
}

.empty-title {
  font-size: 17px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.empty-desc {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.6;
  max-width: 380px;
  margin: 0;
}

.empty-desc strong {
  color: #059669;
  font-weight: 700;
}

/* Categories */
.categories-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.category-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.category-card:hover {
  border-color: #d1d5db;
}

.category-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.category-header:hover {
  background: rgba(0, 0, 0, 0.01);
}

.category-header--active {
  background: rgba(16, 185, 129, 0.02);
}

.category-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(245, 158, 11, 0.08);
  color: #d97706;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.category-header--active .category-icon {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #fff;
}

.category-name {
  flex: 1;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.category-header--active .category-name {
  color: #111827;
}

.category-count {
  font-size: 12px;
  font-weight: 500;
  color: #9ca3af;
  padding: 3px 10px;
  background: #f3f4f6;
  border-radius: 100px;
  white-space: nowrap;
}

.category-chevron {
  color: #9ca3af;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.category-header--active .category-chevron {
  transform: rotate(180deg);
  color: #d97706;
}

/* Category Body */
.category-body {
  border-top: 1px solid #f3f4f6;
  padding: 8px 12px;
}

/* Pekerjaan Items */
.pekerjaan-item {
  border-radius: 10px;
  overflow: hidden;
}

.pekerjaan-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  cursor: pointer;
  border-radius: 10px;
  transition: background 0.15s ease;
}

.pekerjaan-header:hover {
  background: #f9fafb;
}

.pekerjaan-header--active {
  background: #f3f4f6;
}

.pekerjaan-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 8px;
  flex-shrink: 0;
}

.pekerjaan-name {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.pekerjaan-chevron {
  color: #9ca3af;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.pekerjaan-header--active .pekerjaan-chevron {
  transform: rotate(180deg);
  color: #059669;
}

/* Keterangan Items */
.keterangan-list {
  padding: 4px 0 8px 44px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ket-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 12px;
}

.ket-card:hover {
  border-color: rgba(37, 211, 102, 0.4);
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.08);
  transform: translateX(4px);
}

.ket-info {
  flex: 1;
  min-width: 0;
}

.ket-name {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 2px;
  line-height: 1.4;
}

.ket-price {
  font-size: 12px;
  color: #059669;
  font-weight: 600;
  margin: 0;
}

.ket-wa-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(37, 211, 102, 0.1);
  color: #25d366;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.ket-card:hover .ket-wa-btn {
  background: #25d366;
  color: #fff;
}

/* Transitions */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.2s ease;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Bottom Info */
.bottom-info {
  margin-top: 40px;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  background: rgba(245, 158, 11, 0.04);
  border: 1px solid rgba(245, 158, 11, 0.12);
  border-radius: 14px;
}

.info-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
  flex-shrink: 0;
}

.info-title {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 2px;
}

.info-desc {
  font-size: 13px;
  color: #9ca3af;
  margin: 0;
}

/* Responsive */
@media (max-width: 640px) {
  .nuxt-page {
    padding: 80px 16px 40px;
  }

  .page-title {
    font-size: 28px;
  }

  .keterangan-list {
    padding-left: 12px;
  }
}
</style>